import { CareerPathProgress } from '../../types';

export interface CareerPathPrintLevel {
  level: string;
  title: string;
  description: string;
  subLevels: {
    code: string;
    description: string;
  }[];
  goal?: string;
}


const PRINT_STYLES = `
  * { box-sizing: border-box; }
  body {
    margin: 0;
    padding: 24px 32px;
    font-family: 'Open Sans', Arial, sans-serif;
    color: #2C3E35;
    background: #fff;
    font-size: 11pt;
    line-height: 1.5;
  }
  h1 {
    margin: 0 0 6px;
    font-family: 'Oswald', Arial, sans-serif;
    font-size: 22pt;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  .print-meta {
    margin: 0 0 20px;
    color: #555;
    font-size: 10pt;
    border-bottom: 2px solid #F4B41A;
    padding-bottom: 12px;
  }
  .print-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 24px;
    margin-bottom: 24px;
    padding: 12px 16px;
    background: rgba(244, 180, 26, 0.12);
    border-radius: 6px;
    font-size: 10pt;
  }
  .print-summary strong { color: #2C3E35; }
  .level-card {
    break-inside: avoid;
    page-break-inside: avoid;
    margin-bottom: 20px;
    border: 1px solid #ddd;
    border-left: 4px solid #F4B41A;
    border-radius: 6px;
    padding: 14px 16px;
  }
  .level-header {
    display: flex;
    gap: 12px;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e0e0e0;
  }
  .level-badge {
    background: #F4B41A;
    color: #2C3E35;
    font-family: 'Oswald', Arial, sans-serif;
    font-weight: 700;
    font-size: 14pt;
    padding: 6px 12px;
    border-radius: 4px;
    min-width: 44px;
    text-align: center;
    flex-shrink: 0;
  }
  .level-title {
    margin: 0 0 4px;
    font-family: 'Oswald', Arial, sans-serif;
    font-size: 13pt;
    text-transform: uppercase;
  }
  .level-desc {
    margin: 0 0 8px;
    font-style: italic;
    color: #555;
    font-size: 9.5pt;
  }
  .level-progress {
    font-size: 9pt;
    font-weight: 600;
  }
  .sublevel {
    display: flex;
    gap: 10px;
    margin-bottom: 8px;
    padding: 8px 10px;
    background: rgba(244, 180, 26, 0.06);
    border-radius: 4px;
    border-left: 3px solid #F4B41A;
    font-size: 9.5pt;
  }
  .sublevel.completed {
    background: rgba(44, 62, 53, 0.06);
    border-left-color: #2C3E35;
  }
  .sublevel-check {
    flex-shrink: 0;
    width: 18px;
    font-weight: 700;
    font-size: 12pt;
    line-height: 1.2;
  }
  .sublevel.completed .sublevel-check { color: #2C3E35; }
  .sublevel:not(.completed) .sublevel-check { color: #ccc; }
  .sublevel-code {
    flex-shrink: 0;
    font-family: 'Oswald', Arial, sans-serif;
    font-weight: 700;
    min-width: 48px;
    color: #D49B12;
  }
  .sublevel.completed .sublevel-code { color: #2C3E35; }
  .sublevel-text { margin: 0; flex: 1; color: #333; }
  .level-goal {
    margin-top: 10px;
    padding: 10px 12px;
    background: rgba(44, 62, 53, 0.05);
    border-top: 2px solid #2C3E35;
    font-size: 9.5pt;
  }
  .level-goal strong {
    display: block;
    font-family: 'Oswald', Arial, sans-serif;
    text-transform: uppercase;
    font-size: 9pt;
    margin-bottom: 4px;
  }
  .level-goal p { margin: 0; }
  @media print {
    body { padding: 12px 16px; }
    .level-card { page-break-inside: avoid; }
  }
`;

const escapeHtml = (text: string): string => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

const isCompleted = (progress: CareerPathProgress, code: string): boolean => {
  return progress[code]?.completed === true;
};

const getOverallProgress = (
  levels: CareerPathPrintLevel[],
  progress: CareerPathProgress
): { completed: number; total: number } => {
  let completed = 0;
  let total = 0;
  levels.forEach((level) => {
    level.subLevels.forEach((sub) => {
      total += 1;
      if (isCompleted(progress, sub.code)) {
        completed += 1;
      }
    });
  });
  return { completed, total };
};

export const buildCareerPathPrintHtml = (
  employeeName: string,
  levels: CareerPathPrintLevel[],
  progress: CareerPathProgress
): string => {
  const generatedAt = new Date().toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  const overall = getOverallProgress(levels, progress);
  const overallPercent = overall.total > 0
    ? Math.round((overall.completed / overall.total) * 100)
    : 0;

  const levelsHtml = levels.map((level) => {
    const levelCompleted = level.subLevels.filter((sub) => isCompleted(progress, sub.code)).length;
    const levelTotal = level.subLevels.length;
    const sublevelsHtml = level.subLevels.map((sub) => {
      const done = isCompleted(progress, sub.code);
      return `
        <div class="sublevel ${done ? 'completed' : ''}">
          <span class="sublevel-check" aria-hidden="true">${done ? '✓' : '○'}</span>
          <span class="sublevel-code">${escapeHtml(sub.code)}</span>
          <p class="sublevel-text">${escapeHtml(sub.description)}</p>
        </div>
      `;
    }).join('');

    const goalHtml = level.goal
      ? `
        <div class="level-goal">
          <strong>Goal for ${escapeHtml(level.level)}</strong>
          <p>${escapeHtml(level.goal)}</p>
        </div>
      `
      : '';

    return `
      <section class="level-card">
        <div class="level-header">
          <div class="level-badge">${escapeHtml(level.level)}</div>
          <div>
            <h2 class="level-title">${escapeHtml(level.title)}</h2>
            <p class="level-desc">${escapeHtml(level.description)}</p>
            <div class="level-progress">${levelCompleted} / ${levelTotal} competencies completed</div>
          </div>
        </div>
        ${sublevelsHtml}
        ${goalHtml}
      </section>
    `;
  }).join('');

  const safeName = escapeHtml(employeeName);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Career Path - ${safeName}</title>
  <style>${PRINT_STYLES}</style>
</head>
<body>
  <h1>Career Progression Scale</h1>
  <p class="print-meta">L1–L5 Developer Career Path · ${safeName} · Generated ${escapeHtml(generatedAt)}</p>
  <div class="print-summary">
    <span><strong>Overall progress:</strong> ${overall.completed} / ${overall.total} (${overallPercent}%)</span>
  </div>
  ${levelsHtml}
</body>
</html>`;
};

const sanitizeFilename = (name: string): string => {
  return name.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-') || 'developer';
};

const removePrintFrame = (iframe: HTMLIFrameElement): void => {
  if (iframe.parentNode) {
    iframe.parentNode.removeChild(iframe);
  }
};

/**
 * Prints via a hidden iframe so browsers do not block pop-ups.
 */
export const printCareerPathSheet = (
  employeeName: string,
  levels: CareerPathPrintLevel[],
  progress: CareerPathProgress,
  mode: 'print' | 'pdf'
): void => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'Career path print preview');
  iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(iframe);

  const printWindow = iframe.contentWindow;
  const printDoc = printWindow?.document;
  if (!printWindow || !printDoc) {
    removePrintFrame(iframe);
    throw new Error('Unable to prepare print preview.');
  }

  const html = buildCareerPathPrintHtml(employeeName, levels, progress);
  printDoc.open();
  printDoc.write(html);
  printDoc.close();

  if (mode === 'pdf') {
    printDoc.title = `Career-Path-${sanitizeFilename(employeeName)}`;
  }

  let cleaned = false;
  const cleanup = (): void => {
    if (cleaned) {
      return;
    }
    cleaned = true;
    removePrintFrame(iframe);
  };

  const triggerPrint = (): void => {
    printWindow.onafterprint = cleanup;
    printWindow.focus();
    printWindow.print();
    // Some browsers never fire afterprint on iframes
    window.setTimeout(cleanup, 60_000);
  };

  const schedulePrint = (): void => {
    window.setTimeout(triggerPrint, 300);
  };

  if (printDoc.readyState === 'complete') {
    schedulePrint();
  } else {
    iframe.onload = schedulePrint;
  }
};
